/**
 * Testimonial module
 *
 * Blockquote with source and a rounded image
 */

// Deps
import { createComponent, FelaComponent } from "react-fela";
import { gutters, colors, layout } from "~/styleguide";
import { expand, fluid } from "~/styleguide/utils";
import { textStyles } from "~/styleguide/typography";
import { combineRules } from "fela";
import { ContentMaxWidth } from "~/components/layout/max-width";

// Components
import Visual from "~/components/shared/visual";
import WordpressLink from "~/components/global/links";

export default function Testimonial({ block }) {
    const {
        quote,
        source,
        source_title,
        image,
        image_side = "right",
        logo,
        cta_link,
        cta_label = "Learn More",
        testimonial_style,
        text_shape = "circle",
        anchor_id,
        preload
    } = block;

    let isPill = false;

    if (testimonial_style == "pill") {
        isPill = true;
    }

    const CTA = isMobile => {
        return (
            <>
                {cta_link && cta_label && (
                    <WordpressLink
                        label={cta_label}
                        url={cta_link}
                        cta_arrow="true"
                        extend={combineRules(
                            textStyles.paragraphLarge,
                            ctaStyle(isMobile)
                        )}
                    />
                )}
            </>
        );
    };

    const decorativeCircles = () => {
        const isLeftPillDecor = image_side === "right" && text_shape === "pill";
        const isRightPillDecor = image_side === "left" && text_shape === "pill";

        return (
            <>
                <DecorativeCircleWrapper imageSide={image_side}>
                    {(image_side !== "left" || !isPill) && !isLeftPillDecor && (
                        <DecorativeCircle
                            margin={178}
                            multiplyer={-0.25}
                            opacity={0.75}
                        />
                    )}

                    {image_side !== "left" && (
                        <DecorativeCircle
                            margin={isLeftPillDecor ? 0 : 178}
                            multiplyer={isLeftPillDecor ? 0 : 1}
                            opacity={isLeftPillDecor ? 1 : 0.4}
                            extend={{
                                extend: [
                                    {
                                        condition: isLeftPillDecor,
                                        style: {
                                            borderRadius: "0 178px 178px 0",
                                            width: "100%",
                                            maxWidth: 900
                                        }
                                    }
                                ]
                            }}
                        />
                    )}

                    <ImageContainer
                        isPill={isPill}
                        imageSide={image_side}
                        extend={{ left: 180 + 360 + 180 + 360 }}
                    >
                        <Visual
                            load={preload}
                            image={image}
                            fill={true}
                            extend={expand()}
                            background="cover"
                        />
                    </ImageContainer>

                    {(image_side === "left" || isRightPillDecor) && (
                        <DecorativeCircle
                            margin={178}
                            multiplyer={2}
                            opacity={isRightPillDecor ? 1 : 0.4}
                            extend={{
                                extend: [
                                    {
                                        condition: isRightPillDecor,
                                        style: {
                                            borderRadius: "178px 0 0 178px",
                                            width: "100%",
                                            maxWidth: 900
                                        }
                                    }
                                ]
                            }}
                        />
                    )}

                    {(!isPill || image_side === "left") &&
                        !isRightPillDecor && (
                            <DecorativeCircle
                                margin={178}
                                multiplyer={3}
                                direction={"left"}
                                opacity={1}
                                colorOverride="#7549EA"
                                extend={{}}
                            />
                        )}
                </DecorativeCircleWrapper>

                <MobileDecorativeCircleWrapper>
                    <MobileCircle>
                        <Visual
                            load={preload}
                            fill={true}
                            background="cover"
                            image={image}
                            extend={mobileImageStyle}
                        />
                    </MobileCircle>
                </MobileDecorativeCircleWrapper>
            </>
        );
    };

    return (
        <Container id={anchor_id} className="module_testimonial module-testimonial">
            <ContentMaxWidth extend={contentWrapStyles(image_side)}>
                <Content isPill={isPill} imageSide={image_side}>
                    <Visual image={logo} extend={logoStyle} />
                    {/* Quote */}
                    {quote && <blockquote dangerouslySetInnerHTML={{ __html: quote }} />}
                    {/* Source */}
                    {source && (
                        <FelaComponent as="p" style={sourceStyle}>
                            <strong>{source}</strong>
                            {source_title && <span>, {source_title}</span>}
                        </FelaComponent>
                    )}
                    {CTA(false)}
                </Content>
                {decorativeCircles()}
                {CTA(true)}
            </ContentMaxWidth>
        </Container>
    );
}

const mobileImageStyle = {
    borderRadius: "50%",
    height: 120,
    width: 120,
    overflow: "hidden",
    flexShrink: 0,
    position: "relative",
    "& .vv-image": {
        width: 120,
        height: 120
    },
    "& .vv-fill": {
        borderRadius: "50%"
    }
};

const MobileCircle = createComponent(() => ({
    position: "relative",
    width: 120,
    ":before": {
        content: "''",
        position: "absolute",
        border: "1px solid",
        borderColor: colors.blue,
        width: 200,
        height: 120,
        borderRadius: 200,
        right: "120%"
    },
    ":after": {
        content: "''",
        position: "absolute",
        top: 0,
        right: "-120%",
        border: "1px solid",
        borderColor: colors.purple,
        width: 120,
        height: 120,
        borderRadius: 200
    }
}));

const MobileDecorativeCircleWrapper = createComponent(() => ({
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 30,
    minWidthMobileLandscape: {
        visibility: "hidden",
        height: 0,
        overflow: "hidden"
    }
}));

const logoStyle = {
    maxWidth: 125,
    height: "auto",
    marginBottom: 20,
    "& .vv-visual *": {
        height: "auto"
    },
    maxWidthMobileLandscape: {
        marginLeft: "auto",
        marginRight: "auto"
    }
};

const ctaStyle = isMobile => ({
    marginTop: 40,
    display: "block",
    fontWeight: "bold",
    color: colors.blue,
    extend: [
        {
            condition: isMobile,
            style: {
                minWidthMobileLandscape: {
                    visibility: "hidden",
                    height: 0,
                    overflow: "hidden",
                    marginLeft: "auto",
                    marginRight: "auto"
                }
            }
        },
        {
            condition: !isMobile,
            style: {
                maxWidthMobileLandscape: {
                    visibility: "hidden",
                    height: 0,
                    overflow: "hidden"
                }
            }
        }
    ]
});

const sourceStyle = {
    marginTop: 30
};

const contentWrapStyles = imageSide => ({
    display: "flex",
    alignItems: "center",
    margin: "auto",
    width: "100%",
    height: "100%",
    flexWrap: "wrap",
    maxWidthMobileLandscape: {
        justifyContent: "center"
    },

    extend: [
        {
            condition: imageSide === "left",
            style: {
                justifyContent: "flex-end"
            }
        }
    ]
});

const Container = createComponent(
    () => ({
        overflow: "hidden",
        position: "relative",
        display: "flex",
        justifyContent: "space-between",
        minHeight: 520,
        extend: [
            fluid("paddingH", gutters.default),
            fluid("paddingV", gutters.vertical)
        ]
    }),
    "div",
    ["id"]
);

const Content = createComponent(({ imageSide }) => ({
    width: "50%",
    zIndex: 5,
    marginRight: 30,
    maxWidthMobileLandscape: {
        width: "100%",
        marginRight: 0,
        textAlign: "center"
    },
    "> blockquote": {
        ...textStyles.blockquote
    },

    extend: [
        {
            condition: imageSide === "left",
            style: {
                width: "calc(50% - 40px)",
                marginRight: 0
            }
        }
    ]
}));

const ImageContainer = createComponent(({ isPill, imageSide }) => ({
    width: 360,
    height: 360,
    borderRadius: "50%",
    overflow: "hidden",
    position: "absolute",
    top: 0,
    bottom: 0,
    margin: "auto",
    zIndex: 1,
    flexShrink: 0,
    extend: [
        {
            condition: isPill,
            style: {
                width: "50vw",
                maxWidth: layout.siteMaxWidth / 2 - 40,
                borderRadius: "178px 0 0 178px",
                right: 0,
                top: 0,
                bottom: 0,
                margin: "auto",
                position: "absolute"
            }
        },
        {
            condition: !isPill,
            style: {
                "& .vv-fill": {
                    borderRadius: "50%"
                }
            }
        },
        {
            condition: imageSide === "left" && isPill,
            style: {
                right: "18%",
                width: "50vw",
                borderRadius: "0 178px 178px 0",
                left: "auto"
            }
        },
        {
            condition: imageSide === "left" && !isPill,
            style: {
                right: "18%",
                left: "auto"
            }
        }
    ]
}));

const DecorativeCircleWrapper = createComponent(({ imageSide }) => ({
    width: layout.contentMaxWidth,
    margin: "auto",
    position: "absolute",
    left: (178 + 360 * 2) * -1,
    right: 0,
    maxWidthMobileLandscape: {
        visibility: "hidden",
        height: 0,
        overflow: "hidden"
    },

    extend: [
        {
            condition: imageSide === "left",
            style: {
                // border: "2px solid green"
            }
        }
    ]
}));

const DecorativeCircle = createComponent(
    ({ margin, multiplyer, opacity, colorOverride, direction = "left" }) => ({
        borderRadius: "50%",
        border: "1px solid",
        borderColor: colors.blue,
        width: 360,
        height: 360,
        flexShrink: 0,
        top: 0,
        bottom: 0,
        margin: "auto",
        zIndex: 0,
        position: "absolute",
        decorativeCircle: {
            margin: margin,
            multiplyer: multiplyer,
            opacity: opacity,
            direction: direction,
            circleWidth: 360
        },
        extend: [
            {
                condition: colorOverride,
                style: {
                    borderColor: colorOverride
                }
            }
        ]
    })
);
